/**
 * SearchableList — generic popover-based searchable list with keyboard navigation.
 *
 * Provides a trigger button + popover dropdown with:
 * - Auto-search input when item count exceeds `searchThreshold` (default: 10)
 * - Keyboard navigation: Arrow Up/Down to navigate, Enter to select
 * - Auto-highlight first filtered result
 * - Mouse hover syncs highlight index
 * - ScrollIntoView for highlighted items
 * - Customizable item rendering via `renderItem` / `renderTriggerLabel`
 * - Check mark for selected item
 *
 * @example
 * // Basic usage
 * <SearchableList
 *   items={[{ value: "en", label: "English" }, { value: "sv", label: "Svenska" }]}
 *   value={language}
 *   onValueChange={setLanguage}
 *   searchPlaceholder="Search language…"
 * />
 *
 * @example
 * // Custom filter + rendering for rich items
 * <SearchableList
 *   items={languages}
 *   value={value}
 *   onValueChange={setValue}
 *   filterFn={(item, q) => item.native.toLowerCase().includes(q) || item.english.toLowerCase().includes(q)}
 *   renderItem={(item) => `${item.native} — (${item.english})`}
 *   renderTriggerLabel={(item) => item ? item.native : "Select…"}
 * />
 */

import {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  type ReactNode,
} from "react"
import ChevronsUpDownIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-sort-vertical.svg?react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover"
import { cn } from "@/shared/utils/general/cn"
import {
  PickerSearch,
  PickerItem,
  PickerEmpty,
} from "@/shared/components/picker"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimum shape for a list item. Consumers can extend with extra fields. */
export interface SearchableListItem {
  /** Unique value identifier */
  value: string
  /** Display label (also used as default search target) */
  label: string
}

export interface SearchableListProps<T extends SearchableListItem> {
  /** Array of items to display */
  items: T[]
  /** Currently selected value */
  value: string
  /** Called when user selects an item */
  onValueChange: (value: string) => void | Promise<void>

  // -- Search ---------------------------------------------------------------

  /** Placeholder text for the search input */
  searchPlaceholder?: string
  /**
   * Minimum number of items before the search input is shown.
   * Set to `0` to always show search. Default: `10`.
   */
  searchThreshold?: number
  /**
   * Custom filter function. Receives the item and the lowercase query string.
   * Default: case-insensitive match on `item.label` and `item.value`.
   */
  filterFn?: (item: T, query: string) => boolean

  // -- Rendering ------------------------------------------------------------

  /**
   * Custom render for each item row. Receives the item object.
   * Default: renders `item.label`.
   */
  renderItem?: (item: T) => ReactNode
  /**
   * Custom render for the trigger button label.
   * Receives the selected item (or `undefined` if nothing matches).
   * Default: renders `selectedItem.label` or the placeholder.
   */
  renderTriggerLabel?: (selectedItem: T | undefined) => ReactNode
  /** Message shown when filter returns zero results. Default: "No results found" */
  emptyMessage?: string

  // -- Styling & layout -----------------------------------------------------

  /** Extra className for the trigger button */
  triggerClassName?: string
  /** Extra className for the popover content */
  contentClassName?: string
  /** Popover alignment relative to trigger. Default: "start" */
  align?: "start" | "center" | "end"
  /** Disable the entire selector */
  disabled?: boolean
}

// ---------------------------------------------------------------------------
// Default filter: case-insensitive match on label + value
// ---------------------------------------------------------------------------

function defaultFilter<T extends SearchableListItem>(
  item: T,
  query: string,
): boolean {
  return (
    item.label.toLowerCase().includes(query) ||
    item.value.toLowerCase().includes(query)
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SearchableList<T extends SearchableListItem>({
  items,
  value,
  onValueChange,
  searchPlaceholder = "Search…",
  searchThreshold = 10,
  filterFn,
  renderItem,
  renderTriggerLabel,
  emptyMessage = "No results found",
  triggerClassName,
  contentClassName,
  align = "start",
  disabled = false,
}: SearchableListProps<T>) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState("")
  const [highlightIndex, setHighlightIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const showSearch = items.length >= searchThreshold

  // ── Focus search input when popover opens ────────────────────────────
  useEffect(() => {
    if (open && showSearch) {
      const t = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
    if (!open) {
      setFilter("")
      setHighlightIndex(0)
    }
  }, [open, showSearch])

  // ── Ensure wheel scrolling works inside portalled popover ────────────
  // Radix Dialog's scroll-lock (react-remove-scroll) blocks wheel events
  // on elements outside its DOM tree. Since PopoverContent renders in a
  // separate Portal, wheel events get intercepted. We use a callback ref
  // to attach a native wheel handler the instant the DOM element mounts,
  // avoiding timing issues with Portal rendering + useEffect.
  const wheelCleanupRef = useRef<(() => void) | null>(null)

  const scrollContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      // Clean up previous listener
      wheelCleanupRef.current?.()
      wheelCleanupRef.current = null
      listRef.current = el

      if (!el) return

      const onWheel = (e: WheelEvent) => {
        const { scrollTop, scrollHeight, clientHeight } = el
        const maxScroll = scrollHeight - clientHeight

        // Only intercept if the container is actually scrollable
        if (maxScroll <= 0) return

        // Clamp the new scroll position
        const newScrollTop = Math.max(
          0,
          Math.min(maxScroll, scrollTop + e.deltaY),
        )

        // Only act if there's somewhere to scroll
        if (newScrollTop !== scrollTop) {
          el.scrollTop = newScrollTop
        }

        // Always prevent default to stop react-remove-scroll and
        // native scroll from interfering
        e.preventDefault()
        e.stopPropagation()
      }

      el.addEventListener("wheel", onWheel, { passive: false })
      wheelCleanupRef.current = () =>
        el.removeEventListener("wheel", onWheel)
    },
    [], // stable — no deps needed
  )

  // ── Filtered items ───────────────────────────────────────────────────
  const resolveFn = filterFn ?? defaultFilter
  const filtered = useMemo(() => {
    if (!filter) return items
    const q = filter.toLowerCase()
    return items.filter((item) => resolveFn(item, q))
  }, [items, filter, resolveFn])

  // ── Reset highlight on filter change ─────────────────────────────────
  useEffect(() => {
    setHighlightIndex(0)
  }, [filter])

  // ── Scroll highlighted item into view ────────────────────────────────
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const listItems = list.querySelectorAll("[data-searchable-item]")
    const target = listItems[highlightIndex]
    if (target) {
      target.scrollIntoView({ block: "nearest" })
    }
  }, [highlightIndex])

  // ── Resolve selected item (String coercion handles number/string mismatch) ─
  const selectedItem = items.find((i) => i.value === String(value))

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleSelect = useCallback(
    async (item: T) => {
      await onValueChange(item.value)
      setOpen(false)
    },
    [onValueChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === "Enter") {
        e.preventDefault()
        const item = filtered[highlightIndex]
        if (item) {
          void handleSelect(item)
        }
      } else if (e.key === "Escape") {
        e.preventDefault()
        setOpen(false)
      }
    },
    [filtered, highlightIndex, handleSelect],
  )

  // ── Trigger label ────────────────────────────────────────────────────
  const triggerLabel = renderTriggerLabel
    ? renderTriggerLabel(selectedItem)
    : selectedItem?.label ?? searchPlaceholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full max-w-xs items-center justify-between rounded-xl border border-foreground/9 bg-background px-3 py-2",
            "text-sm font-medium text-foreground",
            "hover:border-foreground/39",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          "w-(--radix-popover-trigger-width) p-0",
          contentClassName,
        )}
        align={align}
      >
        {/* ── Single scroll container (search is sticky so wheel works everywhere) */}
        <div
          ref={scrollContainerRef}
          className="max-h-60 overflow-y-auto"
          // When no search input, allow keyboard nav on the list itself
          tabIndex={showSearch ? undefined : 0}
          onKeyDown={showSearch ? undefined : handleKeyDown}
        >
          {/* ── Search filter (sticky header) ───────────────────────── */}
          {showSearch && (
            <PickerSearch
              value={filter}
              onChange={setFilter}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              inputRef={inputRef}
              className="sticky top-0 z-10 border-b-border/50 bg-popover"
            />
          )}

          {/* ── Item list ───────────────────────────────────────────── */}
          <div className="p-1">
          {filtered.length === 0 ? (
            <PickerEmpty>{emptyMessage}</PickerEmpty>
          ) : (
            filtered.map((item, index) => {
              const isSelected = String(value) === item.value
              const isHighlighted = index === highlightIndex

              return (
                <PickerItem
                  key={item.value}
                  data-searchable-item
                  highlighted={isHighlighted}
                  selected={isSelected}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  role="option"
                >
                  {renderItem ? renderItem(item) : item.label}
                </PickerItem>
              )
            })
          )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
